import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { TextField, SelectField } from '@/components/forms/Field';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { enrollmentsApi } from '@/services/api/endpoints/students';
import { academicYearsApi, gradeOfferingsApi, sectionsApi } from '@/services/api/endpoints/academic';
import { useAcademicYearPicker, useCampusPicker } from '@/hooks/usePickers';
import { queryKeys } from '@/services/api/queryKeys';
import type { Student } from '@/types/domain';

const schema = z.object({
  gradeOfferingId: z.string().uuid('Select a grade'),
  sectionId: z.string(),
  rollNumber: z.string().max(32),
  enrollmentDate: z.string().min(1, 'Enrolment date is required'),
  remarks: z.string().max(500),
});

type FormValues = z.infer<typeof schema>;

/**
 * Places a student into a grade for an academic year.
 *
 * The pickers cascade: choosing a campus narrows the grades, and choosing a
 * grade narrows the sections. The server enforces the same relationships and
 * rejects an inconsistent combination — this only keeps the user from being
 * offered one.
 */
export function EnrollStudentModal({ open, student, onClose, onEnrolled }: {
  open: boolean;
  student: Student;
  onClose: () => void;
  onEnrolled: () => void;
}) {
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      gradeOfferingId: '',
      sectionId: '',
      rollNumber: '',
      enrollmentDate: new Date().toISOString().slice(0, 10),
      remarks: '',
    },
  });

  // Year and campus are local filters, not submitted: they exist only to narrow
  // the offering list to combinations the school actually runs.
  const [yearId, setYearId] = useState('');
  const [campusId, setCampusId] = useState('');
  const gradeOfferingId = form.watch('gradeOfferingId');

  const years = useAcademicYearPicker();
  const campuses = useCampusPicker();

  const currentYear = useQuery({
    queryKey: queryKeys.academicYears.current,
    queryFn: () => academicYearsApi.current(),
    enabled: open,
  });

  const offerings = useQuery({
    queryKey: queryKeys.gradeOfferings.list({ enrol: true, yearId, campusId }),
    queryFn: () =>
      gradeOfferingsApi.list({
        page: 0,
        size: 100,
        status: 'ACTIVE',
        academicYearId: yearId,
        campusId,
      }),
    enabled: open && yearId !== '' && campusId !== '',
  });

  const sections = useQuery({
    queryKey: queryKeys.sections.list({ enrol: true, gradeOfferingId }),
    queryFn: () =>
      sectionsApi.list({ page: 0, size: 100, sort: 'displayOrder,asc', gradeOfferingId, status: 'ACTIVE' }),
    enabled: open && gradeOfferingId !== '',
  });

  // Default to the school's current year: it is what the user almost always wants.
  useEffect(() => {
    if (open && currentYear.data && yearId === '') {
      setYearId(currentYear.data.id);
    }
  }, [open, currentYear.data, yearId]);

  // An offering under the previous year or campus is no longer valid, and a
  // section under the previous offering is not either.
  useEffect(() => {
    form.setValue('gradeOfferingId', '');
    form.setValue('sectionId', '');
  }, [yearId, campusId, form]);

  useEffect(() => {
    form.setValue('sectionId', '');
  }, [gradeOfferingId, form]);

  const enrol = useMutation({
    mutationFn: (values: FormValues) =>
      enrollmentsApi.create({
        studentId: student.id,
        gradeOfferingId: values.gradeOfferingId,
        sectionId: values.sectionId === '' ? undefined : values.sectionId,
        rollNumber: values.rollNumber === '' ? undefined : values.rollNumber,
        enrollmentDate: values.enrollmentDate,
        remarks: values.remarks === '' ? undefined : values.remarks,
      }),
    onSuccess: () => {
      toast.success(`${student.fullName} enrolled`);
      form.reset();
      void queryClient.invalidateQueries({ queryKey: queryKeys.enrollments.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.students.all });
      onEnrolled();
    },
    onError: (error) => handleApiError(error, form.setError),
  });

  const submit = form.handleSubmit((values) => enrol.mutate(values));
  const { errors } = form.formState;

  return (
    <Modal
      open={open}
      title={`Enrol ${student.fullName}`}
      description="A student may hold at most one enrolment per academic year."
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} loading={enrol.isPending}>
            Enrol
          </Button>
        </>
      }
    >
      <form className="space-y-4" noValidate onSubmit={submit}>
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label="Academic year"
            required
            value={yearId}
            onChange={(event) => setYearId(event.target.value)}
          >
            <option value="">Select a year…</option>
            {(years.data ?? [])
              // A finished year is history and rejects new enrolments.
              .filter((year) => year.status === 'PLANNED' || year.status === 'ACTIVE')
              .map((year) => (
                <option key={year.id} value={year.id}>
                  {year.name}
                  {year.current ? ' (current)' : ''}
                </option>
              ))}
          </SelectField>

          <SelectField
            label="Campus"
            required
            value={campusId}
            onChange={(event) => setCampusId(event.target.value)}
          >
            <option value="">Select a campus…</option>
            {(campuses.data ?? []).map((campus) => (
              <option key={campus.id} value={campus.id}>
                {campus.name}
              </option>
            ))}
          </SelectField>
        </div>

        <SelectField
          label="Grade"
          required
          disabled={yearId === '' || campusId === ''}
          hint={
            yearId === '' || campusId === ''
              ? 'Select a year and campus first'
              : (offerings.data?.data ?? []).length === 0
                ? 'No grades are offered at that campus for that year'
                : undefined
          }
          error={errors.gradeOfferingId?.message}
          {...form.register('gradeOfferingId')}
        >
          <option value="">Select a grade…</option>
          {(offerings.data?.data ?? []).map((offering) => (
            <option key={offering.id} value={offering.id}>
              {offering.grade.name}
            </option>
          ))}
        </SelectField>

        <SelectField
          label="Section"
          disabled={gradeOfferingId === ''}
          hint="Optional — a section can be assigned later"
          error={errors.sectionId?.message}
          {...form.register('sectionId')}
        >
          <option value="">Not assigned</option>
          {(sections.data?.data ?? []).map((section) => (
            <option key={section.id} value={section.id}>
              {section.name}
            </option>
          ))}
        </SelectField>

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="Roll number"
            hint="Unique among active enrolments in the section"
            error={errors.rollNumber?.message}
            {...form.register('rollNumber')}
          />
          <TextField
            label="Enrolment date"
            type="date"
            required
            error={errors.enrollmentDate?.message}
            {...form.register('enrollmentDate')}
          />
        </div>

        <TextField label="Remarks" error={errors.remarks?.message} {...form.register('remarks')} />
      </form>
    </Modal>
  );
}
